import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import {
  apiClient,
  BuildingDetailsLoaderResult,
} from "~/services/apiClient";
import {
  _BuildingPageTabType,
  FilesReturnType,
  FormattedServiceRequestData,
  FormattedUnitData,
} from "~/types";
import {
  ENVS,
  INTENTS,
  getFilteredUnitList,
  resolveActorUserName,
  getFilteredRequestList,
  loadCookiesData,
} from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import { S3ServiceLambda } from "~/services/s3";

export const BuildingDetailsPageLoader = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<BuildingDetailsPageLoaderType | Response> => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;
  const { searchParams } = new URL(request.url);
  const tab = (searchParams.get("tab") ?? "details") as _BuildingPageTabType;

  const buildingID = params.buildingID || "";

  const result = await apiClient.loader<BuildingDetailsLoaderResult>(
    "building-details",
    {
      id: buildingID,
      ownerID,
      ownerType,
      userID: user.uid,
    }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load building details:", result.error);
    return redirect("/building");
  }

  const data = result.data;
  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const formattedServiceRequestList = getFilteredRequestList({
    filters: {},
    requestList: data.formattedServiceRequestList as FormattedServiceRequestData[],
    isAdminOrSuperAdmin,
  });

  const filteredUnitList = getFilteredUnitList({
    filters: {},
    unitList: data.unitList as FormattedUnitData[],
    isAdminOrSuperAdmin,
  });

  const userName = await resolveActorUserName(user, authAccessInfo);

  const files = await S3ServiceLambda.list({
    entityType: APITypes.EntityType.Building,
    entityID: buildingID,
  });

  return {
    tab,
    buildingDetails: data.buildingDetails,
    propertyDetails: data.propertyDetails,
    unitList: filteredUnitList,
    formattedServiceRequestList,
    tenantList: data.tenantList,
    ownerID,
    ownerType,
    userName,
    files: files.data,
    canCreateRequest: data.permissions.canCreateServiceRequest,
    canDeleteBuilding: data.permissions.canDeleteBuilding,
    canEditBuilding: data.permissions.canEditBuilding,
    canReadBuilding: data.permissions.canReadBuilding,
    isAdminOrSuperAdmin,
    isAuthenticated: true,
    subscriptionPlan: data.subscriptionPlan,
    hasServiceRequestAccess: data.hasServiceRequestAccess,
  };
};

export type BuildingDetailsPageLoaderType = {
  tab: _BuildingPageTabType;
  buildingDetails: APITypes.Building;
  propertyDetails: APITypes.Property;
  unitList: FormattedUnitData[];
  formattedServiceRequestList: FormattedServiceRequestData[];
  tenantList: APITypes.Tenant[];
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  files: FilesReturnType;
  canCreateRequest: boolean;
  canDeleteBuilding: boolean;
  canEditBuilding: boolean;
  canReadBuilding: boolean;
  isAdminOrSuperAdmin: boolean;
  isAuthenticated: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasServiceRequestAccess: boolean;
};

export const BuildingDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const buildingID = params.buildingID || "";
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");

  const { ownerID, ownerType } = authAccessInfo.data;
  const userName = await resolveActorUserName(user, authAccessInfo);
  console.log("action called. intent", intent);

  switch (intent) {
    case INTENTS._edit_building: {
      return withEntityCacheInvalidation(
        [{ entity: "building", entityID: buildingID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<APITypes.Building>(
            INTENTS._edit_building,
            { id: buildingID, ...payload },
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          const baseError = result.error ?? "Failed to update building";
          const withTrail =
            result.debugLogs?.length ?
              `${baseError} | ${result.debugLogs.join(" | ")}`
            : baseError;
          return {
            success: false,
            data: {} as APITypes.Building,
            error: withTrail,
          };
        },
      );
    }
    case INTENTS._remove_building: {
      return withEntityCacheInvalidation(
        [{ entity: "building", entityID: buildingID }],
        async () => {
          const body = formData.get("body") as string;
          const payload = body ? (JSON.parse(body) as { _version?: number }) : {};
          const result = await apiClient.sendIntent<{ deleted: boolean; id: string }>(
            INTENTS._remove_building,
            { id: buildingID, _version: payload._version },
          );
          if (result.success) {
            return redirect("/building");
          }
          console.error("Remove building failed:", result.error);
          return redirect(`/building/${buildingID}`);
        },
      );
    }
    case INTENTS._add_service_request: {
      const payload = JSON.parse(
        formData.get("body") as string
      ) as Record<string, unknown>;

      const serviceRequestPayload = {
        ...payload,
        creatorID: user?.uid ?? "",
        creatorName: userName,
        ownerID: user?.uid ?? "",
        ownerType: APITypes.OwnerType.User,
      };
      const result = await apiClient.sendIntent<APITypes.ServiceRequest>(
        INTENTS._add_service_request,
        serviceRequestPayload
      );
      if (result.success && result.data) {
        return { success: true, data: result.data };
      }
      return {
        success: false,
        data: {} as APITypes.ServiceRequest,
        error: result.error ?? "Failed to create service request",
      };
    }
    case INTENTS._delete_file: {
      const key: string = formData.get("body") as string;
      const result = await S3ServiceLambda.remove({ key });
      return result;
    }
  }
  return null;
};
